<script>
import { mapGetters, mapActions } from 'vuex';
import MensagemItem from './MensagemItem.vue';

export default {
  name: 'MensagemList',
  components: { MensagemItem },
  props: {
    mensagens: { type: Array, default: () => [] },
  },
  data() {
    return {
      mostrarBotaoBaixo: false,
      // Quando true, o próximo update das mensagens NÃO deve rolar pra baixo
      // (usado depois de prepend de página anterior — mantemos âncora visual).
      preservandoScroll: false,
      // Distância (em px) do TOPO de antes da prepend — usada pra restaurar.
      ancoraScroll: 0,
      // Bloqueia disparos múltiplos enquanto carrega
      carregandoAnteriores: false,
    };
  },
  computed: {
    ...mapGetters('atendimento', ['paginacaoDaAtiva']),
    temMaisMensagens() { return Boolean(this.paginacaoDaAtiva.temMais); },
    /**
     * Agrupa mensagens por data: { 'Hoje': [m1,m2], 'Ontem': [m3], '12/04/2026': [m4] }
     */
    mensagensAgrupadas() {
      const grupos = [];
      let chaveAtual = null;
      let bucket = null;

      for (const m of this.mensagens) {
        const data = m.recebida_em || m.enviada_em || m.created_at;
        if (!data) continue;
        const chave = this._chaveData(data);
        if (chave !== chaveAtual) {
          bucket = { chave, mensagens: [] };
          grupos.push(bucket);
          chaveAtual = chave;
        }
        bucket.mensagens.push(m);
      }
      return grupos;
    },
  },
  watch: {
    mensagens: {
      handler() {
        this.$nextTick(() => {
          if (this.preservandoScroll) {
            this._restaurarAncora();
            this.preservandoScroll = false;
          } else {
            this.rolarParaFim();
          }
        });
      },
      deep: true,
    },
  },
  mounted() {
    this.rolarParaFim();
    if (this.$refs.scroll) this.$refs.scroll.addEventListener('scroll', this.onScroll);
  },
  beforeUnmount() {
    if (this.$refs.scroll) this.$refs.scroll.removeEventListener('scroll', this.onScroll);
  },
  methods: {
    ...mapActions('atendimento', ['fetchMensagensAnteriores']),
    rolarParaFim() {
      const el = this.$refs.scroll;
      if (!el) return;
      el.scrollTop = el.scrollHeight;
      // Mídias (imagens, vídeos) aumentam scrollHeight depois de carregadas
      // — sem isso o usuário fica preso acima do fim quando abre a conversa.
      this._reAncorarAposMidias();
    },
    _reAncorarAposMidias() {
      const el = this.$refs.scroll;
      if (!el) return;
      const midias = el.querySelectorAll('img, video');
      midias.forEach((m) => {
        const jaCarregada = (m.tagName === 'IMG' && m.complete)
          || (m.tagName === 'VIDEO' && m.readyState >= 1);
        if (jaCarregada) return;
        const handler = () => {
          const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
          if (dist < 250) el.scrollTop = el.scrollHeight;
          m.removeEventListener('load', handler);
          m.removeEventListener('loadedmetadata', handler);
          m.removeEventListener('error', handler);
        };
        m.addEventListener('load', handler);
        m.addEventListener('loadedmetadata', handler);
        m.addEventListener('error', handler);
      });
    },
    onScroll() {
      const el = this.$refs.scroll;
      if (!el) return;
      const distanciaDoFim = el.scrollHeight - el.scrollTop - el.clientHeight;
      this.mostrarBotaoBaixo = distanciaDoFim > 200;

      // Próximo do topo (< 80px) e ainda há histórico → carrega mais antigas
      if (el.scrollTop < 80 && this.temMaisMensagens && !this.carregandoAnteriores) {
        this._carregarAnteriores();
      }
    },
    async _carregarAnteriores() {
      const el = this.$refs.scroll;
      if (!el) return;
      this.carregandoAnteriores = true;
      // Marca a posição atual antes do prepend; após o prepend, usamos a
      // diferença de scrollHeight pra reposicionar e manter o usuário fixo
      // visualmente na mesma mensagem.
      this.preservandoScroll = true;
      this.ancoraScroll = el.scrollHeight - el.scrollTop;
      try {
        await this.fetchMensagensAnteriores();
      } catch (_e) {
        this.preservandoScroll = false;
      } finally {
        this.carregandoAnteriores = false;
      }
    },
    _restaurarAncora() {
      const el = this.$refs.scroll;
      if (!el) return;
      // Após prepend, o scrollHeight cresceu — recoloca o usuário na mesma
      // mensagem ajustando scrollTop pelo delta.
      el.scrollTop = el.scrollHeight - this.ancoraScroll;
    },
    _chaveData(data) {
      const d = new Date(data);
      const hoje = new Date();
      const ontem = new Date();
      ontem.setDate(hoje.getDate() - 1);
      if (this._mesmaData(d, hoje)) return 'Hoje';
      if (this._mesmaData(d, ontem)) return 'Ontem';
      // Outros dias dessa semana: dia da semana ("Segunda-feira")
      const diffDias = Math.floor((hoje - d) / (1000 * 60 * 60 * 24));
      if (diffDias < 7) {
        return d.toLocaleDateString('pt-BR', { weekday: 'long' });
      }
      // Mais antigos: data completa
      return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
    },
    _mesmaData(a, b) {
      return a.getFullYear() === b.getFullYear()
        && a.getMonth() === b.getMonth()
        && a.getDate() === b.getDate();
    },
  },
};
</script>

<template>
  <div class="mensagem-list-wrapper">
    <div ref="scroll" class="mensagem-list">
      <div v-if="!mensagens.length" class="text-center text-danger my-5">
        <i class="ri-chat-3-line" style="font-size: 2rem;"></i>
        <div class="mt-2">Nenhuma mensagem ainda.</div>
      </div>
      <template v-else>
        <!-- Indicador no topo (lazy-load das anteriores) -->
        <div v-if="carregandoAnteriores" class="topo-carregando">
          <i class="ri-loader-4-line spin me-1"></i>Carregando mensagens anteriores...
        </div>
        <div v-else-if="!temMaisMensagens" class="topo-fim">Início da conversa</div>

        <div v-for="grupo in mensagensAgrupadas" :key="grupo.chave">
          <div class="separador-data">
            <span>{{ grupo.chave }}</span>
          </div>
          <MensagemItem v-for="msg in grupo.mensagens" :key="msg.id" :mensagem="msg" />
        </div>
      </template>
    </div>

    <button
      v-if="mostrarBotaoBaixo"
      type="button"
      class="btn btn-primary btn-baixo shadow"
      title="Ir pra última mensagem"
      @click="rolarParaFim"
    >
      <i class="ri-arrow-down-line"></i>
    </button>
  </div>
</template>

<style scoped>
.mensagem-list-wrapper {
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.mensagem-list {
  flex: 1;
  overflow-y: auto;
  padding: 1rem 1.25rem;
  background-color: var(--mensagem-list-bg, #fcf8f4);
  /* Doodles do PCdoB em SVG. `local` faz o padrão acompanhar o scroll e
     repetir em toda a altura da conversa (sem isso, ele para na altura do
     container visível). `repeat` cobre tanto X quanto Y. */
  background-image: var(--mensagem-list-pattern, url('~@/assets/img/chat-bg-light.svg'));
  background-repeat: repeat;
  background-size: 280px 280px;
  background-attachment: local;
}

:global([data-bs-theme="dark"]) .mensagem-list {
  --mensagem-list-bg: #03060a;
  --mensagem-list-pattern: url('~@/assets/img/chat-bg-dark.svg');
}

:global([data-bs-theme="dark"]) .separador-data span {
  background: #1d242c;
  border-color: #2a3139;
  color: #b9c2cd;
}

.separador-data {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 1rem 0 0.75rem;
}
.separador-data span {
  background: var(--vz-body-bg);
  color: var(--vz-secondary-color);
  border: 1px solid var(--vz-border-color);
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: capitalize;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.btn-baixo {
  position: absolute;
  right: 1rem;
  bottom: 1rem;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.topo-carregando, .topo-fim {
  text-align: center;
  font-size: 0.75rem;
  color: var(--vz-secondary-color);
  padding: 0.5rem 0;
  margin-bottom: 0.25rem;
}
.topo-fim { opacity: 0.6; font-style: italic; }
.spin { display: inline-block; animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

</style>
